import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n } from 'react-redux-i18n';
import CircularProgress from '@material-ui/core/CircularProgress';
import Chip from '@material-ui/core/Chip';
import Checkbox from '@material-ui/core/Checkbox';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import SvgIcon from './SvgIcon';
import * as productStockAction from '../actions/productStock.action';
import ProductStockMovementForm from './ProductStockMovementForm';
import PurchasePriceBulkEdit from './PurchasePriceBulkEdit';
import { confirm } from '../utils';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import { getVariableServer } from '../selectors/global.selector';

const FILTERS = [
	{ key: 'managed',   labelKey: 'productStock.filterManaged' },
	{ key: 'in_stock',  labelKey: 'productStock.filterInStock' },
	{ key: 'no_stock',  labelKey: 'productStock.filterNoStock' },
	{ key: 'unmanaged', labelKey: 'productStock.filterUnmanaged' },
];

class ProductStockList extends Component {
	constructor(props) {
		super(props);
		this.state = {
			movementFormOpen: false,
			selectedProduct: null,
			filter: 'managed',
			confirmRemoveProduct: null,
			editingStockId: null,
			editingValue: '',
			editingPurchasePriceId: null,
			editingPurchasePriceValue: '',
			selectedIds: [],
			bulkEditOpen: false,
		};
	}

	componentDidMount() {
		const { id_menu, stocks, typeMovements } = this.props;
		if (!stocks.length) {
			this.props.productStockAction.getProductStock(id_menu);
		}
		if (!typeMovements.length) {
			this.props.productStockAction.getTypeMovements();
		}
	}

	handleCellClick = (product, stockData) => {
		const endStock = Math.round(Number(stockData.end_of_service_stock));
		this.setState({ editingStockId: product.id_global, editingValue: String(endStock) });
	};

	handleCellChange = e => {
		const val = e.target.value;
		if (val === '' || /^\d+$/.test(val)) {
			this.setState({ editingValue: val });
		}
	};

	handleCellCommit = (product, stockData) => {
		const { editingValue } = this.state;
		this.setState({ editingStockId: null, editingValue: '' });

		const newValue = parseInt(editingValue, 10);
		if (isNaN(newValue) || newValue < 0) return;

		const currentEnd = Math.round(Number(stockData.end_of_service_stock));
		const delta = newValue - currentEnd;
		if (delta === 0) return;

		const isEntry = delta > 0;
		const adjustmentType = (this.props.typeMovements || []).find(
			t => Number(t.direction) === (isEntry ? 1 : 0) &&
			t.label.toLowerCase().includes('ajustement')
		);
		if (!adjustmentType) return;

		this.props.productStockAction.createMovement({
			id_product: product.id_global,
			product_name: product.name,
			id_type_movement: adjustmentType.id,
			quantity: Math.abs(delta),
			comment: '',
			unit_value: 0,
			total_value: 0,
			delta,
			id_menu: this.props.id_menu,
		});
	};

	// "Prix d'achat HT" cell — same inline-edit pattern as the "QTE FIN"
	// cell above, but commits through setPurchasePrice (upserts the
	// quantity:0 product_movement row) rather than createMovement.
	handlePurchasePriceCellClick = (product, stockData) => {
		const value = stockData.purchase_price;
		this.setState({
			editingPurchasePriceId: product.id_global,
			editingPurchasePriceValue:
				value === null || value === undefined ? '' : Number(value).toFixed(2),
		});
	};

	handlePurchasePriceCellChange = e => {
		let val = e.target.value;
		val = val.replace(/,/g, '.');
		val = val.replace(/[^.\d]/g, '');
		this.setState({ editingPurchasePriceValue: val });
	};

	handlePurchasePriceCellCommit = (product, stockData) => {
		const { editingPurchasePriceValue } = this.state;
		this.setState({ editingPurchasePriceId: null, editingPurchasePriceValue: '' });

		if (editingPurchasePriceValue === '') {
			if (stockData.purchase_price !== null && stockData.purchase_price !== undefined) {
				this.props.productStockAction.deletePurchasePrice(product.id_global);
			}
			return;
		}
		const parsed = parseFloat(editingPurchasePriceValue);
		if (!Number.isFinite(parsed) || parsed === Number(stockData.purchase_price)) return;

		this.props.productStockAction.setPurchasePrice(product.id_global, parsed);
	};

	openMovementForm = product => {
		this.props.productStockAction.getMovements(product.id_global);
		this.setState({ movementFormOpen: true, selectedProduct: product });
	};

	closeMovementForm = () => {
		this.setState({ movementFormOpen: false, selectedProduct: null });
	};

	handleSubmitMovement = data => {
		this.props.productStockAction.createMovement({ ...data, id_menu: this.props.id_menu });
	};

	// selected_date arrive au format 'YYYY-MM-DD HH:mm:ss' ; les formulaires
	// ne manipulent que le jour.
	getSelectedDay = () => {
		const raw = this.props.selected_date;
		return typeof raw === 'string' && raw.length >= 10 ? raw.slice(0, 10) : null;
	};

	// Prix d'achat seul, depuis le "+" d'un produit sans suivi : on écrit le
	// prix sans jamais créer de product_quantity_info, le produit reste donc
	// sans gestion de stock.
	handleSubmitPurchasePriceOnly = ({ id_product, unit_value, effective_day }) => {
		this.props.productStockAction.setPurchasePrice(id_product, unit_value, effective_day);
	};

	handleDeleteMovement = (id_movement, id_product, delta) => {
		this.props.productStockAction.deleteMovement(id_movement, id_product, delta, this.props.id_menu);
	};

	handleConfirmRemove = () => {
		const { confirmRemoveProduct } = this.state;
		if (confirmRemoveProduct) {
			this.props.productStockAction.removeFromStock(confirmRemoveProduct.id_global, this.props.id_menu);
		}
		this.setState({ confirmRemoveProduct: null });
	};

	// Selection for the bulk "Prix d'achat HT" edit is always scoped to the
	// products currently visible in the table — i.e. respecting whichever
	// status filter chip (managed/in_stock/no_stock/unmanaged) is active.
	toggleSelect = id_global => {
		this.setState(prevState => ({
			selectedIds: prevState.selectedIds.includes(id_global)
				? prevState.selectedIds.filter(id => id !== id_global)
				: [...prevState.selectedIds, id_global],
		}));
	};

	toggleSelectAllVisible = filteredProducts => {
		const visibleIds = filteredProducts.map(p => p.id_global);
		const allSelected = visibleIds.every(id => this.state.selectedIds.includes(id));
		this.setState({
			selectedIds: allSelected
				? this.state.selectedIds.filter(id => !visibleIds.includes(id))
				: Array.from(new Set([...this.state.selectedIds, ...visibleIds])),
		});
	};

	selectAllVisible = filteredProducts => {
		const visibleIds = filteredProducts.map(p => p.id_global);
		this.setState({
			selectedIds: Array.from(new Set([...this.state.selectedIds, ...visibleIds])),
		});
	};

	deselectAll = () => {
		this.setState({ selectedIds: [] });
	};

	handleBulkValid = items => {
		this.props.productStockAction.bulkSetPurchasePrice(items);
		this.setState({ bulkEditOpen: false, selectedIds: [] });
	};

	// "Effacer le prix d'achat HT" — same hard-delete as clearing a single
	// cell (handlePurchasePriceCellCommit), applied to every selected
	// product that actually has a price set. Only offered when at least one
	// selected product has one.
	handleBulkDelete = idsWithPrice => {
		const label = I18n.t('productStock.bulkPurchasePrice.deleteConfirmation', {
			n: idsWithPrice.length,
		});
		confirm(label).then(() => {
			this.props.productStockAction.bulkDeletePurchasePrice(idsWithPrice);
			this.setState({ selectedIds: [] });
		});
	};

	getStockData(product) {
		const stockRow = this.props.stocks.find(s => s.id_global === product.id_global);
		if (!stockRow) return { current_stock: 0, end_of_service_stock: 0, id_global: product.id_global, pending_sum: 0, id_stock: null, purchase_price: null };
		const endOfService = Number(stockRow.current_stock) + Number(stockRow.pending_sum);
		return { ...stockRow, end_of_service_stock: endOfService };
	}

	isManaged(stockData) {
		return stockData.id_stock !== null && stockData.id_stock !== undefined;
	}

	getFilteredProducts() {
		const { products } = this.props;
		const { filter } = this.state;
		return products.filter(product => {
			const sd = this.getStockData(product);
			const managed = this.isManaged(sd);
			switch (filter) {
				case 'managed':   return managed;
				case 'in_stock':  return managed && Number(sd.current_stock) > 0;
				case 'no_stock':  return managed && Number(sd.current_stock) === 0;
				case 'unmanaged': return !managed;
				default:          return true;
			}
		});
	}

	resolveTax(id_tax_rate) {
		const tax = (this.props.taxes || []).find(t => t.id === id_tax_rate);
		return tax ? tax.name : '—';
	}

	resolveType(id_product_type) {
		const pt = (this.props.productTypes || []).find(t => t.id === id_product_type);
		return pt ? pt.name : '—';
	}

	resolvePlace(id_place_send) {
		const ps = (this.props.placeSends || []).find(p => p.id === id_place_send);
		return ps ? ps.name : '—';
	}

	resolveColor(id_color) {
		const c = (this.props.colors || []).find(c => c.id === id_color);
		return c ? c.color : '#ccc';
	}

	resolveCategories(categoryIds) {
		if (!categoryIds || !categoryIds.length) return '—';
		return categoryIds.map(id => {
			const cat = (this.props.categories || []).find(c => c.id === id);
			return cat ? cat.name : '';
		}).filter(Boolean).join(', ') || '—';
	}

	render() {
		const { loading, typeMovements, canEdit } = this.props;
		const { movementFormOpen, selectedProduct, filter, confirmRemoveProduct, selectedIds, bulkEditOpen } = this.state;

		if (loading) {
			return (
				<div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
					<CircularProgress />
				</div>
			);
		}

		const filteredProducts = this.getFilteredProducts();
		const selectedWithStock = selectedProduct ? this.getStockData(selectedProduct) : null;
		const visibleIds = filteredProducts.map(p => p.id_global);
		const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));
		const someVisibleSelected = !allVisibleSelected && visibleIds.some(id => selectedIds.includes(id));
		const selectedProducts = this.props.products.filter(p => selectedIds.includes(p.id_global));
		const selectedIdsWithPrice = selectedProducts
			.filter(p => {
				const price = this.getStockData(p).purchase_price;
				return price !== null && price !== undefined;
			})
			.map(p => p.id_global);

		return (
			<div className="stockTable">
				<div className="stock-filters">
					{FILTERS.map(f => (
						<Chip
							key={f.key}
							label={I18n.t(f.labelKey)}
							onClick={() => this.setState({ filter: f.key })}
							color={filter === f.key ? 'primary' : 'default'}
							style={{ marginRight: 8, marginBottom: 8 }}
						/>
					))}
				</div>

				<div className="stock-disclaimer">
					<span className="stock-disclaimer-icon">ℹ</span>
					<span>{I18n.t('productStock.disclaimer')}</span>
				</div>

				<p className="stock-footnote">{I18n.t('productStock.footnote')}</p>

				{canEdit && (
					<div className="stock-selection-toolbar">
						<Button
							size="small"
							variant="outlined"
							color="primary"
							onClick={() => this.selectAllVisible(filteredProducts)}>
							{I18n.t('productStock.selectAllVisible')}
						</Button>
						{selectedIds.length > 0 && (
							<>
								<Button size="small" variant="outlined" onClick={this.deselectAll}>
									{I18n.t('productStock.deselectAll')}
								</Button>
								<span className="stock-selection-count">
									{I18n.t('productStock.selectedCount', { n: selectedIds.length })}
								</span>
								<Button
									size="small"
									variant="raised"
									color="primary"
									onClick={() => this.setState({ bulkEditOpen: true })}>
									{I18n.t('productStock.bulkEditButton')}
								</Button>
								{selectedIdsWithPrice.length > 0 && (
									<Button
										size="small"
										variant="outlined"
										color="secondary"
										onClick={() => this.handleBulkDelete(selectedIdsWithPrice)}>
										{I18n.t('productStock.bulkDeleteButton')}
									</Button>
								)}
							</>
						)}
					</div>
				)}

				<table>
					<thead>
						<tr>
							{canEdit && (
								<th className="stock-th-checkbox">
									<Checkbox
										checked={allVisibleSelected}
										indeterminate={someVisibleSelected}
										onChange={() => this.toggleSelectAllVisible(filteredProducts)}
									/>
								</th>
							)}
							<th className="stock-th-color" />
							<th className="stock-th-name">{I18n.t('product.name').toUpperCase()}</th>
							<th className="stock-th-std">{I18n.t('productStock.colPrice').toUpperCase()}</th>
							<th className="stock-th-num stock-th-purchase-price">
								{I18n.t('productStock.colPurchasePrice').toUpperCase()}
								{/* Même lien que dans la fiche produit : à quoi sert ce
								    prix et comment lire la rentabilité qui en découle. */}
								<Tooltip title={I18n.t('product.form.purchase_price_help')}>
									<a
										className={'purchase-price-doc'}
										href={
											'https://laddition.slite.page/p/sNMdikG3Y2wwQi/Analyser-la-rentabilite-d-un-produit'
										}
										target={'_blank'}
										rel={'noopener noreferrer'}>
										<InfoIcon fontSize={'inherit'} />
									</a>
								</Tooltip>
							</th>
							<th className="stock-th-std">{I18n.t('productStock.colTax').toUpperCase()}</th>
							<th className="stock-th-std">{I18n.t('productStock.colType').toUpperCase()}</th>
							<th className="stock-th-std">{I18n.t('productStock.colCategories').toUpperCase()}</th>
							<th className="stock-th-std">{I18n.t('productStock.colPlace').toUpperCase()}</th>
							<th className="stock-th-std">{I18n.t('productStock.colFollowType').toUpperCase()}</th>
							<th className="stock-th-num">{I18n.t('productStock.currentStock').toUpperCase()} *</th>
							<th className="stock-th-num">{I18n.t('productStock.endOfServiceStock').toUpperCase()} *</th>
							<th className="stock-th-action" />
						</tr>
					</thead>
					<tbody>
						{filteredProducts.map((product, index) => {
							const stockData = this.getStockData(product);
							const endStock = stockData.end_of_service_stock;
							const isLow = this.isManaged(stockData) && Number(endStock) <= 0;
							const rowClass = !product.active ? 'handson-disabled' : '';
							return (
								<tr key={product.id} className={rowClass}>
									{canEdit && (
										<td className="stock-td-checkbox">
											<Checkbox
												checked={selectedIds.includes(product.id_global)}
												onChange={() => this.toggleSelect(product.id_global)}
											/>
										</td>
									)}
									<td className="stock-td-color">
										<span
											className="stock-color-dot"
											style={{ backgroundColor: this.resolveColor(product.id_color) }}
										/>
									</td>
									<td className="stock-td-name">{product.name}</td>
									<td className="stock-td-std">{product.price}</td>
									<td className="stock-td-num stock-td-purchase-price">
										{this.state.editingPurchasePriceId === product.id_global ? (
											<input
												type="text"
												className="stock-cell-input"
												value={this.state.editingPurchasePriceValue}
												onChange={this.handlePurchasePriceCellChange}
												onBlur={() => this.handlePurchasePriceCellCommit(product, stockData)}
												onKeyDown={e => {
													if (e.key === 'Enter') this.handlePurchasePriceCellCommit(product, stockData);
													if (e.key === 'Escape') this.setState({ editingPurchasePriceId: null });
												}}
												autoFocus
											/>
										) : (
											<span
												className="stock-cell-editable"
												onClick={() => this.handlePurchasePriceCellClick(product, stockData)}>
												{/* Aucun mouvement : on affiche 0.00 plutôt qu'un tiret.
												    Retirer un prix d'achat se fait en posant un
												    mouvement à 0, les deux situations doivent donc se
												    lire pareil. */}
												{stockData.purchase_price !== null && stockData.purchase_price !== undefined
													? Number(stockData.purchase_price).toFixed(2)
													: (0).toFixed(2)}
											</span>
										)}
									</td>
									<td className="stock-td-std">{this.resolveTax(product.id_tax_rate)}</td>
									<td className="stock-td-std">{this.resolveType(product.id_product_type)}</td>
									<td className="stock-td-std">{this.resolveCategories(product.categories)}</td>
									<td className="stock-td-std">{this.resolvePlace(product.id_place_send)}</td>
									<td className="stock-td-std">{this.isManaged(stockData) ? (stockData.follow_type === 'service' ? 'Service' : 'Global') : '—'}</td>
									<td className="stock-td-num">{this.isManaged(stockData) ? stockData.current_stock : '—'}</td>
									<td className={`stock-td-num${isLow ? ' stock-low' : ''}`}>
										{this.isManaged(stockData) ? (
											this.isManaged(stockData) && stockData.follow_type !== 'service' ? (
												this.state.editingStockId === product.id_global ? (
													<input
														type="number"
														className="stock-cell-input"
														value={this.state.editingValue}
														onChange={this.handleCellChange}
														onBlur={() => this.handleCellCommit(product, stockData)}
														onKeyDown={e => {
															if (e.key === 'Enter') this.handleCellCommit(product, stockData);
															if (e.key === 'Escape') this.setState({ editingStockId: null });
														}}
														min="0"
														step="1"
														autoFocus
													/>
												) : (
													<span
														className="stock-cell-editable"
														onClick={() => this.handleCellClick(product, stockData)}>
														{endStock}
													</span>
												)
											) : endStock
										) : '—'}
									</td>
									<td className="stock-td-action">
										{canEdit && (
											<div className="stock-action-wrap">
												{this.isManaged(stockData) ? (
													<>
														<button
															className="stock-btn stock-btn-edit"
															onClick={() => this.openMovementForm({ ...product, ...stockData })}>
															<SvgIcon icon="editer" />
														</button>
														<button
															className="stock-btn stock-btn-remove"
															onClick={() => this.setState({ confirmRemoveProduct: product })}>
															<DeleteIcon />
														</button>
													</>
												) : (
													<button
														className="stock-btn stock-btn-active"
														onClick={() => this.openMovementForm({ ...product, ...stockData })}>
														<AddIcon />
													</button>
												)}
											</div>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>

				<Dialog
					open={!!confirmRemoveProduct}
					onClose={() => this.setState({ confirmRemoveProduct: null })}
					maxWidth="xs"
					fullWidth>
					<DialogTitle>{I18n.t('productStock.confirmRemoveTitle')}</DialogTitle>
					<DialogContent>
						<DialogContentText>
							{I18n.t('productStock.confirmRemoveText', { name: confirmRemoveProduct ? confirmRemoveProduct.name : '' })}
							{' '}{I18n.t('productStock.confirmRemoveSub')}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => this.setState({ confirmRemoveProduct: null })}>
							{I18n.t('common.cancel')}
						</Button>
						<Button onClick={this.handleConfirmRemove} color="secondary" variant="raised">
							{I18n.t('productStock.confirmRemoveBtn')}
						</Button>
					</DialogActions>
				</Dialog>

				<ProductStockMovementForm
					open={movementFormOpen}
					product={selectedWithStock}
					typeMovements={typeMovements}
					movements={this.props.movements}
					movementsLoading={this.props.movementsLoading}
					selectedDay={this.getSelectedDay()}
					onClose={this.closeMovementForm}
					onSubmit={this.handleSubmitMovement}
					onSubmitPurchasePrice={this.handleSubmitPurchasePriceOnly}
					onDeleteMovement={this.handleDeleteMovement}
				/>

				<PurchasePriceBulkEdit
					open={bulkEditOpen}
					selectedProducts={selectedProducts}
					taxes={this.props.taxes}
					onCancel={() => this.setState({ bulkEditOpen: false })}
					onValid={this.handleBulkValid}
				/>
			</div>
		);
	}
}

const mapStateToProps = state => ({
	// Jour de travail de l'app ('YYYY-MM-DD HH:mm:ss' en base) : sert de date
	// d'effet par défaut aux formulaires de mouvement et de prix d'achat.
	selected_date: getVariableServer(state.entity.variableServer, 'selected_date'),
	stocks: state.productStock.stocks,
	typeMovements: state.productStock.typeMovements,
	movements: state.productStock.movements,
	movementsLoading: state.productStock.movementsLoading,
	loading: state.productStock.loading,
	taxes: state.entity.tax,
	productTypes: state.entity.productType,
	placeSends: state.entity.placeSend,
	categories: state.entity.category.categories,
	colors: state.entity.color,
});

const mapDispatchToProps = dispatch => ({
	productStockAction: bindActionCreators(productStockAction, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductStockList);
